import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { EventApp } from '@app/v2/event/types/event-app.type'
import { CreateSnoballConfigurationPayload, SnoballConfiguration, SnoballFormOption } from '@app/v2/event/types/snoball.type'
import { environment } from '@environments/environment'
import { Observable, of, switchMap, throwError } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import { SelfSettingsService } from './self-settings.service'

type EventFormRow = Record<string, any> & { id: number; options?: Record<string, unknown> | unknown[] }

@Injectable()
export class SnoballService {
  private readonly _selfSettingsService = inject(SelfSettingsService)
  private readonly _authService = inject(AuthService)
  private readonly _integrationService = inject(IntegrationService)
  private readonly _personaService = inject(PersonaService)
  private readonly _http = inject(HttpClient)

  /**
   * Lists Snöball configurations from persona form `options.snoball` (GET forms).
   * Empty `{}` and incomplete mappings are skipped; personas that share project, campaign, and enable become one row.
   * @param {number} eventId - Event id.
   * @returns {Observable<SnoballConfiguration[]>}
   */
  public listConfigurations(eventId: number): Observable<SnoballConfiguration[]> {
    return this._getEventForms(eventId).pipe(map((forms) => this._configurationsFromForms(forms)))
  }

  /**
   * Builds the modal header icon from the event app logo.
   * @param {EventApp | null} app - The Snöball event app.
   * @returns {string} Safe HTML for `headerIcon`.
   */
  public headerIconHtml(app: EventApp | null): string {
    const service = app?.location_details?.service
    const logo = service?.logo
    if (!logo) return ''
    const src = logo.replace(/"/g, '&quot;')
    const title = (service.title ?? 'Snöball').replace(/"/g, '&quot;')
    return `<img src="${src}" alt="${title}" width="20" height="20" class="tw-h-5 tw-w-5 tw-object-contain" />`
  }

  /**
   * Persists a new Snöball mapping on the selected persona forms without charging the wallet.
   * @param {number} eventId - Event id.
   * @param {CreateSnoballConfigurationPayload} payload - Form values.
   * @returns {Observable<void>}
   */
  public addConfiguration(eventId: number, payload: CreateSnoballConfigurationPayload): Observable<void> {
    return this.listConfigurations(eventId).pipe(
      switchMap((existing) => {
        const linkedPersonaIds = new Set(existing.flatMap((config) => config.persona_ids))
        if (payload.persona_ids.some((personaId) => linkedPersonaIds.has(personaId))) {
          return throwError(() => new Error('One persona can only be linked to one configuration.'))
        }

        const projectId = payload.project_id.trim()
        const campaignId = payload.campaign_id.trim()
        const next: SnoballConfiguration = {
          id: this._configurationId(projectId, campaignId, 'active'),
          persona_ids: payload.persona_ids,
          project_id: projectId,
          campaign_id: campaignId,
          status: 'active'
        }
        return this._saveConfigurations(eventId, [...existing, next])
      })
    )
  }

  /**
   * Updates an existing Snöball mapping on persona forms without charging the wallet.
   * @param {number} eventId - Event id.
   * @param {string} configurationId - Configuration to update.
   * @param {CreateSnoballConfigurationPayload} payload - Form values.
   * @returns {Observable<void>}
   */
  public updateConfiguration(eventId: number, configurationId: string, payload: CreateSnoballConfigurationPayload): Observable<void> {
    return this.listConfigurations(eventId).pipe(
      switchMap((existing) => {
        const current = existing.find((config) => config.id === configurationId)
        if (!current) return throwError(() => new Error('Configuration not found.'))

        const reservedPersonaIds = new Set(existing.filter((config) => config.id !== configurationId).flatMap((config) => config.persona_ids))
        if (payload.persona_ids.some((personaId) => reservedPersonaIds.has(personaId))) {
          return throwError(() => new Error('One persona can only be linked to one configuration.'))
        }

        const projectId = payload.project_id.trim()
        const campaignId = payload.campaign_id.trim()
        const next = existing.map((config) =>
          config.id === configurationId
            ? {
                ...config,
                id: this._configurationId(projectId, campaignId, config.status),
                persona_ids: payload.persona_ids,
                project_id: projectId,
                campaign_id: campaignId
              }
            : config
        )
        return this._saveConfigurations(eventId, next)
      })
    )
  }

  /**
   * Toggles a configuration between active and inactive on persona forms without charging the wallet.
   * @param {number} eventId - Event id.
   * @param {string} configurationId - Configuration to update.
   * @param {'active' | 'inactive'} status - Next status.
   * @returns {Observable<void>}
   */
  public toggleConfigurationStatus(eventId: number, configurationId: string, status: 'active' | 'inactive'): Observable<void> {
    const saved$ = this.listConfigurations(eventId).pipe(
      switchMap((existing) => {
        const current = existing.find((config) => config.id === configurationId)
        if (!current) return throwError(() => new Error('Configuration not found.'))
        const next = existing.map((config) =>
          config.id === configurationId
            ? {
                ...config,
                id: this._configurationId(config.project_id, config.campaign_id, status),
                status
              }
            : config
        )
        return this._saveConfigurations(eventId, next)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )
    // The modal is destroyed on close, which would abort a subscriber-only request. This subscription keeps the save running.
    saved$.subscribe({ error: () => undefined })
    return saved$
  }

  /**
   * Removes one configuration. Personas that no longer have a mapping get `options.snoball: {}`.
   * @param {number} eventId - Event id.
   * @param {string} configurationId - Configuration to remove.
   * @returns {Observable<void>}
   */
  public disconnectConfiguration(eventId: number, configurationId: string): Observable<void> {
    return this.listConfigurations(eventId).pipe(
      switchMap((existing) => {
        const next = existing.filter((config) => config.id !== configurationId)
        return this._saveConfigurations(eventId, next)
      })
    )
  }

  /**
   * Writes `options.snoball` as `{}` on every persona form for the event.
   * The key stays on the form; an inactive flag is not a removal.
   * @param {number} eventId - Event id.
   * @returns {Observable<unknown>}
   */
  public clearEventPersonaSnoball(eventId: number): Observable<unknown> {
    return this._syncPersonaFormOptions(eventId, [], true)
  }

  /**
   * Sends the Snöball activation Slack message and records that the request was sent.
   * @param {number} organizationId - Workspace id.
   * @param {number} eventId - Event id.
   * @param {EventApp} app - The Snöball event app row.
   * @param {string} eventName - Event name for Slack.
   * @param {string} eventSlug - Event slug for Slack.
   * @returns {Observable<EventApp>}
   */
  public requestActivation(organizationId: number, eventId: number, app: EventApp, eventName: string, eventSlug: string): Observable<EventApp> {
    return this._sendActivationSlack(eventId, eventName, eventSlug).pipe(switchMap(() => this._saveActivationRequested(organizationId, eventId, app)))
  }

  /**
   * PUTs `options.snoball` on persona forms. Add, edit, status toggle, and disconnect all use this path — none charge the wallet.
   * @param {number} eventId - Event id.
   * @param {SnoballConfiguration[]} configurations - Next configuration list.
   * @returns {Observable<void>}
   */
  private _saveConfigurations(eventId: number, configurations: SnoballConfiguration[]): Observable<void> {
    return this._syncPersonaFormOptions(eventId, configurations).pipe(map(() => undefined))
  }

  /**
   * PUTs selected persona forms via `PersonaService.updateForms`, same as the persona editor.
   * PATCH `/forms` is a different policy and returns "This action is unauthorized."
   * @param {number} eventId - Event id.
   * @param {SnoballConfiguration[]} configurations - Current Snöball mappings.
   * @param {boolean} replaceMissingWithEmptyObject - When true, personas with no mapping get `options.snoball: {}`.
   * @returns {Observable<unknown>}
   */
  private _syncPersonaFormOptions(eventId: number, configurations: SnoballConfiguration[], replaceMissingWithEmptyObject = false): Observable<unknown> {
    const optionByPersonaId = this._formOptionByPersonaId(configurations)
    return this._getEventForms(eventId).pipe(
      switchMap((forms) =>
        this._getEventPhysicalTypeId(eventId).pipe(
          switchMap((physicalTypeId) => {
            const updates = forms
              .map((form) => this._prepareFormPut(form, optionByPersonaId.get(form.id), physicalTypeId, replaceMissingWithEmptyObject))
              .filter((form): form is EventFormRow => form != null)
            return this._personaService.updateForms(eventId, updates)
          })
        )
      )
    )
  }

  /**
   * Builds table rows from forms that already have a non-empty `options.snoball`.
   * @param {EventFormRow[]} forms - Event persona forms.
   * @returns {SnoballConfiguration[]}
   */
  private _configurationsFromForms(forms: EventFormRow[]): SnoballConfiguration[] {
    const groups = new Map<string, SnoballConfiguration>()
    for (const form of forms) {
      const option = this._readSnoballOption(form.options)
      if (!option || form.id == null) continue
      const status: SnoballConfiguration['status'] = option.enable ? 'active' : 'inactive'
      const id = this._configurationId(option.project_id, option.campaign_id, status)
      const existing = groups.get(id)
      if (existing) {
        existing.persona_ids.push(form.id)
        continue
      }
      groups.set(id, {
        id,
        persona_ids: [form.id],
        project_id: option.project_id,
        campaign_id: option.campaign_id,
        status
      })
    }
    return [...groups.values()]
  }

  /**
   * Parses a form's `options.snoball`. Empty `{}` and whitespace-only ids are treated as no mapping.
   * @param {unknown} options - Stored form options.
   * @returns {SnoballFormOption | null}
   */
  private _readSnoballOption(options: unknown): SnoballFormOption | null {
    const snoball = this._asFormOptions(options)['snoball']
    if (!snoball || typeof snoball !== 'object' || Array.isArray(snoball)) return null
    const row = snoball as Record<string, unknown>
    const projectId = typeof row.project_id === 'string' ? row.project_id.trim() : ''
    const campaignId = typeof row.campaign_id === 'string' ? row.campaign_id.trim() : ''
    if (!projectId || !campaignId) return null
    return {
      project_id: projectId,
      campaign_id: campaignId,
      enable: row.enable !== false
    }
  }

  /**
   * Stable row id derived from the form option fields that define a mapping.
   * @param {string} projectId - Snöball project id.
   * @param {string} campaignId - Snöball campaign id.
   * @param {'active' | 'inactive'} status - Mapped from `enable`.
   * @returns {string}
   */
  private _configurationId(projectId: string, campaignId: string, status: 'active' | 'inactive'): string {
    return `${encodeURIComponent(projectId)}::${encodeURIComponent(campaignId)}::${status}`
  }

  /**
   * Maps each linked persona to the Snöball object written on `form.options`.
   * @param {SnoballConfiguration[]} configurations - Current mappings.
   * @returns {Map<number, SnoballFormOption>}
   */
  private _formOptionByPersonaId(configurations: SnoballConfiguration[]): Map<number, SnoballFormOption> {
    const optionByPersonaId = new Map<number, SnoballFormOption>()
    for (const config of configurations) {
      const option: SnoballFormOption = {
        project_id: config.project_id,
        enable: config.status === 'active',
        campaign_id: config.campaign_id
      }
      for (const personaId of config.persona_ids) optionByPersonaId.set(personaId, option)
    }
    return optionByPersonaId
  }

  /**
   * Builds a PUT form the same way `PersonaService.save` does, then sets `options.snoball`.
   * @param {EventFormRow} form - Current persona form from GET.
   * @param {SnoballFormOption | undefined} nextOption - Mapping to persist. No mapping stores `options.snoball` as `{}` when the form already has one.
   * @param {number | null} physicalTypeId - Event physical type.
   * @param {boolean} replaceMissingWithEmptyObject - When true, every persona with no mapping gets `options.snoball: {}`, including forms that never had the key.
   * @returns {EventFormRow | null}
   */
  private _prepareFormPut(
    form: EventFormRow,
    nextOption: SnoballFormOption | undefined,
    physicalTypeId: number | null,
    replaceMissingWithEmptyObject = false
  ): EventFormRow | null {
    const options = this._asFormOptions(form.options)
    const current = options['snoball']
    let nextOptions = options
    if (nextOption) {
      if (JSON.stringify(current) === JSON.stringify(nextOption)) return null
      nextOptions = { ...options, snoball: nextOption }
    } else if (replaceMissingWithEmptyObject || current) {
      if (JSON.stringify(current) === '{}') return null
      nextOptions = { ...options, snoball: {} }
    } else {
      return null
    }

    const next: EventFormRow = { ...form, options: nextOptions }
    if (!next.parent) delete next.parent
    if (next.enable_payment && !next.registration_amount) next.registration_amount = 0
    if (!next.enable_payment) delete next.registration_amount
    delete next.promo_codes

    const formEmail = next.form_email && typeof next.form_email === 'object' ? next.form_email : {}
    next.name = next.name ?? ''
    next.slug = next.slug ?? ''
    next.type = next.type ?? 10
    next.weight = next.weight ?? 0
    next.physical_type_id = next.physical_type_id ?? physicalTypeId ?? 1
    next.promo_code_ids = Array.isArray(next.promo_code_ids) ? next.promo_code_ids : []
    next.sub_locations_ids = Array.isArray(next.sub_locations_ids)
      ? next.sub_locations_ids
      : Array.isArray(next.checkinIds)
      ? next.checkinIds
      : Array.isArray(next.checkins)
      ? next.checkins.map((checkin: { id?: number }) => checkin?.id).filter((id: number | undefined) => id != null)
      : []
    next.email_from_name = next.email_from_name || formEmail.email_from_name || ''
    next.email_subject = next.email_subject || formEmail.email_subject || ''
    return next
  }

  /**
   * Loads event forms without the getEventForms transform that nulls `registration_amount`.
   * @param {number} eventId - Event id.
   * @returns {Observable<EventFormRow[]>}
   */
  private _getEventForms(eventId: number): Observable<EventFormRow[]> {
    return this._http.get<unknown>(`${environment.planningApi}forms?event_id=${eventId}`).pipe(
      map((res) => {
        if (Array.isArray(res)) return res as EventFormRow[]
        if (res && typeof res === 'object' && Array.isArray((res as { data?: unknown }).data)) {
          return (res as { data: EventFormRow[] }).data
        }
        return []
      })
    )
  }

  /**
   * Loads the event physical type required by PUT /forms when the GET form omits it.
   * @param {number} eventId - Event id.
   * @returns {Observable<number | null>}
   */
  private _getEventPhysicalTypeId(eventId: number): Observable<number | null> {
    return this._http.get<{ physical_type_id?: number }>(`${environment.planningApi}events/find?event_id=${eventId}`).pipe(
      map((event) => event?.physical_type_id ?? null),
      catchError(() => of(null))
    )
  }

  /**
   * Normalizes `form.options` to a plain object (some rows store an empty array).
   * @param {unknown} options - Stored form options.
   * @returns {Record<string, unknown>}
   */
  private _asFormOptions(options: unknown): Record<string, unknown> {
    if (options && typeof options === 'object' && !Array.isArray(options)) return { ...(options as Record<string, unknown>) }
    return {}
  }

  /**
   * Persists that an activation request was sent for this event.
   * @param {number} organizationId - Workspace id.
   * @param {number} eventId - Event id.
   * @param {EventApp} app - The Snöball event app row.
   * @returns {Observable<EventApp>}
   */
  private _saveActivationRequested(organizationId: number, eventId: number, app: EventApp): Observable<EventApp> {
    return this._selfSettingsService
      .updateEventApp(organizationId, eventId, app.id, {
        name: app.name,
        location_details: {
          ...app.location_details,
          snoball_activation_requested: true
        }
      })
      .pipe(map((res) => res.data))
  }

  /**
   * Sends the Snöball activation Slack notification required by INT-261.
   * @param {number} eventId - Event id.
   * @param {string} eventName - Event name.
   * @param {string} eventSlug - Event slug.
   * @returns {Observable<unknown>}
   */
  private _sendActivationSlack(eventId: number, eventName: string, eventSlug: string): Observable<unknown> {
    const workspace = this._authService.currentWorkspace
    const user = this._authService.user
    if (!workspace || !user) return throwError(() => new Error('Could not send activation request. Missing event or workspace details.'))

    let message =
      `Snöball Activation Request\n` +
      `>• Event name: ${eventName}\n` +
      `>• Event ID: ${eventId}\n` +
      `>• Event slug: ${eventSlug}\n` +
      `>• Workspace name: ${workspace.name}\n` +
      `>• Requested by: ${user.full_name}\n` +
      `>• Email: ${user.email}\n`
    if (user.phone_number) message += `>• Phone number: ${user.phone_number}`
    return this._integrationService.sendSlackMessage({ message })
  }
}
